import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Search, Edit, Trash2, Download, FileText, Upload, X, File, CheckCircle, AlertCircle,
  Folder, FolderOpen, FolderPlus, ChevronRight, ArrowLeft, Home, Eye, Settings, MoreVertical,
  Grid, List, RefreshCw, ChevronDown
} from 'lucide-react';
import { formsAPI } from '../../lib/api.js';
import Card from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Badge from '../../components/ui/Badge.jsx';
import { formatDate } from '../../utils/formatters.js';
import { hasCapability } from '../../lib/rbac.js';
import { getUser, refreshAuth } from '../../lib/auth.js';
import BulkUploadForms from './BulkUploadForms.jsx';

function FormsPage() {
  // View and navigation state
  const [viewMode, setViewMode] = useState('hierarchical'); // 'hierarchical', 'list'
  const [currentPath, setCurrentPath] = useState([]); // Breadcrumb navigation
  const [currentLevel, setCurrentLevel] = useState('categories'); // 'categories', 'subcategories', 'sub-subcategories'
  const [currentParentId, setCurrentParentId] = useState(null);
  const [currentParent, setCurrentParent] = useState(null);

  // Filters and pagination
  const [filters, setFilters] = useState({
    search: '',
    language: '',
    categoryId: '',
    subcategoryId: '',
    subSubcategoryId: '',
    isPublic: undefined,
    dateFrom: '',
    dateTo: '',
    
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Modal and form state
  const [showModal, setShowModal] = useState(false);
  const [isModalClosing, setIsModalClosing] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [modalType, setModalType] = useState('upload'); // 'upload', 'edit-metadata', 'category', 'subcategory', 'sub-subcategory'
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    language: 'en',
    isPublic: true,
    file: null,
    categoryId: '',
    subcategoryId: '',
    subSubcategoryId: '',
    presign: '',
  });
  const [dragActive, setDragActive] = useState(false);
  const [fileError, setFileError] = useState('');
  const [showBulkUpload, setShowBulkUpload] = useState(false);

  // Category management state
  const [categoryData, setCategoryData] = useState({
    name: '',
    slug: '',
    description: '',
    icon: '',
    color: '#3B82F6',
    sortOrder: 0,
    isActive: true
  });
  const [editingCategory, setEditingCategory] = useState(null);
  const [parentCategory, setParentCategory] = useState(null);
  const [categoryErrors, setCategoryErrors] = useState({});
  const [isSubmittingCategory, setIsSubmittingCategory] = useState(false);

  const queryClient = useQueryClient();

  // Utility functions for category management
  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
  };

  const validateCategoryData = (data, type) => {
    const errors = {};
    
    // Name validation
    if (!data.name || data.name.trim().length === 0) {
      errors.name = 'Name is required';
    } else if (data.name.length > 24) {
      errors.name = 'Name must be less than 24 characters';
    }
    
    // Slug validation
    if (!data.slug || data.slug.trim().length === 0) {
      errors.slug = 'Slug is required';
    } else if (!/^[a-z0-9-]+$/.test(data.slug)) {
      errors.slug = 'Slug can only contain lowercase letters, numbers, and hyphens';
    } else if (data.slug.length > 100) {
      errors.slug = 'Slug must be less than 100 characters';
    }
    
    // Description validation
    if (data.description && data.description.length > 500) {
      errors.description = 'Description must be less than 500 characters';
    }
    
    // Color validation
    if (data.color && !/^#[0-9A-Fa-f]{6}$/.test(data.color)) {
      errors.color = 'Color must be a valid hex color (e.g., #3B82F6)';
    }
    
    // Sort order validation
    if (data.sortOrder < 0) {
      errors.sortOrder = 'Sort order must be a positive number';
    }
    
    return errors;
  };

  // Auto-refresh auth data if user has no role
  React.useEffect(() => {
    const user = getUser();

    // If user has no role, try to refresh auth data
    if (user && !user.role && !user.role_name) {
      refreshAuth().then(result => {
        if (result.success) {
          // Reload the page to get updated user data
          window.location.reload();
        }
      });
    }
  }, []);


  // Fetch hierarchical category tree data
  const { data: categoryTreeData, isLoading: categoriesLoading } = useQuery({
    queryKey: ['forms-categories-tree'],
    queryFn: () => formsAPI.getCategoryTree({ language: 'en' }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });


  // State to track if we need to set form data after modal opens
  const [pendingFormData, setPendingFormData] = React.useState(null);

  // Set form data after modal opens to ensure proper timing
  React.useEffect(() => {
    if (showModal && modalType === 'upload' && pendingFormData) {
      setFormData(pendingFormData);
      setPendingFormData(null);
    }
  }, [showModal, modalType, pendingFormData]);

  // Auto-generate slug when name changes (only for new categories)
  React.useEffect(() => {
    if (!editingCategory && categoryData.name && !categoryData.slug) {
      const generatedSlug = generateSlug(categoryData.name);
      setCategoryData(prev => ({ ...prev, slug: generatedSlug }));
    }
  }, [categoryData.name, editingCategory]);


  // Fetch forms based on current navigation and filters
  const { data: forms, isLoading: formsLoading, error: formsError } = useQuery({
    queryKey: ['forms', filters, pagination.page, pagination.limit, currentPath],
    queryFn: async () => {
      const cleanFilters = { ...filters };

      // Apply navigation-based filters
      if (currentPath.length > 0) {
        const currentItem = currentPath[currentPath.length - 1];
        if (currentItem.type === 'category') {
          cleanFilters.categoryId = currentItem.id;
        } else if (currentItem.type === 'subcategory') {
          cleanFilters.subcategoryId = currentItem.id;
        } else if (currentItem.type === 'sub-subcategory') {
          cleanFilters.subSubcategoryId = currentItem.id;
        }
      }

      // Clean up empty filters
      Object.keys(cleanFilters).forEach(key => {
        if (cleanFilters[key] === '' || cleanFilters[key] === undefined) {
          delete cleanFilters[key];
        }
      });

      const queryParams = {
        ...cleanFilters,
        page: pagination.page,
        limit: pagination.limit
      };

      // Use hierarchical API if any category filters are provided
      const useHierarchical = cleanFilters.categoryId || cleanFilters.subcategoryId || cleanFilters.subSubcategoryId;
      return useHierarchical ? formsAPI.getHierarchical(queryParams) : formsAPI.getAll(queryParams);
    },
    // Removed refetchInterval to prevent unnecessary API calls
    // refetchInterval: 30000,
  });

  // Get current level data based on navigation
  const getCurrentLevelData = () => {
    if (!categoryTreeData?.data?.data) return [];

    if (currentLevel === 'categories') {
      return Array.isArray(categoryTreeData.data.data) ? categoryTreeData.data.data : [];
    } else if (currentLevel === 'subcategories' && currentParentId) {
      const parentCategory = categoryTreeData.data.data.find(cat => cat.id === currentParentId);
      return Array.isArray(parentCategory?.subcategories) ? parentCategory.subcategories : [];
    } else if (currentLevel === 'sub-subcategories' && currentParentId) {
      // Find the subcategory in the tree
      for (const category of categoryTreeData.data.data) {
        const subcategory = category.subcategories?.find(sub => sub.id === currentParentId);
        
        if (subcategory) {
          return Array.isArray(subcategory.subSubcategories) ? subcategory.subSubcategories : [];
        }
      }
      return [];
    }
    return [];
  };

  const currentData = getCurrentLevelData();
  const formsData = forms?.data?.data?.forms || [];

  // Navigation helper functions
  const navigateTo = (item, type) => {
    const newPath = [...currentPath, { type, id: item.id, name: item.name, fullObject: item }];
    setCurrentPath(newPath);
    setCurrentParentId(item.id);
    setCurrentParent(item);

    if (type === 'category') {
      setCurrentLevel('subcategories');
    } else if (type === 'subcategory') {
      setCurrentLevel('sub-subcategories');
    }

    // If the item has forms but no subcategories/sub-subcategories, switch to list view
    if (item.formCount > 0 &&
      ((type === 'category' && (!item.subcategories || item.subcategories.length === 0)) ||
        (type === 'subcategory' && (!item.subSubcategories || item.subSubcategories.length === 0)) ||
        (type === 'sub-subcategory'))) {
      setViewMode('list');
    }
  };

  const navigateBack = () => {
    if (currentPath.length === 0) return;

    const newPath = currentPath.slice(0, -1);
    setCurrentPath(newPath);

    if (newPath.length === 0) {
      setCurrentLevel('categories');
      setCurrentParentId(null);
      setCurrentParent(null);
      setViewMode('hierarchical');
    } else {
      const parentItem = newPath[newPath.length - 1];
      setCurrentParentId(parentItem.id);
      setCurrentParent(parentItem.fullObject);

      if (parentItem.type === 'category') {
        setCurrentLevel('subcategories');
      } else if (parentItem.type === 'subcategory') {
        setCurrentLevel('sub-subcategories');
      }
      setViewMode('hierarchical');
    }
  };

  const navigateToRoot = () => {
    setCurrentPath([]);
    setCurrentLevel('categories');
    setCurrentParentId(null);
    setCurrentParent(null);
    setViewMode('hierarchical');
  };

  // Update pagination when data changes
  useEffect(() => {
    if (forms?.data?.pagination) {
      const paginationData = forms.data.pagination;
      const calculatedPages = paginationData.pages || Math.ceil(paginationData.total / paginationData.limit);
      setPagination({
        ...paginationData,
        pages: calculatedPages
      });
    }
  }, [forms]);

  // Form handling functions
  const resetForm = () => {
    setFormData({
      title: '',
      category: '',
      language: 'en',
      isPublic: true,
      file: null,
      categoryId: '',
      subcategoryId: '',
      subSubcategoryId: '',
    });
    setEditingForm(null);
    setFileError('');
    setDragActive(false);
  };

  const resetCategoryForm = (clearParent = true) => {
    setCategoryData({
      name: '',
      slug: '',
      description: '',
      icon: '',
      color: '#3B82F6',
      sortOrder: 0,
      isActive: true
    });
    if (clearParent) {
      setParentCategory(null);
    }
    setCategoryErrors({});
    setIsSubmittingCategory(false);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      language: '',
      categoryId: '',
      subcategoryId: '',
      subSubcategoryId: '',
      isPublic: undefined,
      dateFrom: '',
      dateTo: '',
    });
  };

  const hasActiveFilters = () => {
    return filters.search || filters.language || filters.categoryId ||
      filters.subcategoryId || filters.subSubcategoryId ||
      filters.isPublic !== undefined || filters.dateFrom || filters.dateTo;
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const createMutation = useMutation({
    mutationFn: (formData) => formsAPI.create(formData),
    onSuccess: () => {
      queryClient.invalidateQueries(['forms']);
      queryClient.invalidateQueries(['forms-categories-tree']);
      setShowModal(false);
      resetForm();
      alert('Form created successfully!');
    },
    onError: (error) => {
      console.error('Create mutation error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error details:', error.response?.data?.error);
      alert('Failed to create form: ' + (error.response?.data?.error?.message || error.message));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => formsAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['forms']);
      queryClient.invalidateQueries(['forms-categories-tree']);
      setShowModal(false);
      resetForm();
      alert('Form updated successfully!');
    },
    onError: (error) => {
      console.error('Update mutation error:', error);
      alert('Failed to update form: ' + (error.response?.data?.error?.message || error.message));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => formsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['forms']);
      queryClient.invalidateQueries(['forms-categories-tree']);
      alert('Form deleted successfully!');
    },
    onError: (error) => {
      console.error('Delete mutation error:', error);
      alert('Failed to delete form: ' + (error.response?.data?.error?.message || error.message));
    },
  });

  // Category creation mutations with enhanced error handling
  const createCategoryMutation = useMutation({
    mutationFn: (data) => formsAPI.categories.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['forms-categories']);
      queryClient.invalidateQueries(['forms-categories-tree']);
      setShowModal(false);
      resetCategoryForm();
      
      // Show success message
      const categoryName = response.data?.data?.category?.name || 'Category';
      alert(`✅ ${categoryName} created successfully!`);
    },
    onError: (error) => {
      console.error('Create category error:', error);
      setIsSubmittingCategory(false);
      
      // Handle validation errors
      if (error.response?.status === 400 && error.response?.data?.error?.message) {
        const errorMessage = error.response.data.error.message;
        if (errorMessage.includes('slug')) {
          setCategoryErrors({ slug: 'This slug is already taken. Please choose a different one.' });
        } else if (errorMessage.includes('name')) {
          setCategoryErrors({ name: 'This name is already taken. Please choose a different one.' });
        } else {
          alert(`❌ Failed to create category: ${errorMessage}`);
        }
      } else {
        alert(`❌ Failed to create category: ${error.response?.data?.error?.message || error.message}`);
      }
    },
  });

  const createSubcategoryMutation = useMutation({
    mutationFn: (data) => formsAPI.subcategories.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['forms-categories']);
      queryClient.invalidateQueries(['forms-categories-tree']);
      setShowModal(false);
      resetCategoryForm();
      
      // Show success message
      const subcategoryName = response.data?.data?.subcategory?.name || 'Subcategory';
      alert(`✅ ${subcategoryName} created successfully!`);
    },
    onError: (error) => {
      console.error('Create subcategory error:', error);
      setIsSubmittingCategory(false);
      
      // Handle validation errors
      if (error.response?.status === 400 && error.response?.data?.error?.message) {
        const errorMessage = error.response.data.error.message;
        if (errorMessage.includes('slug')) {
          setCategoryErrors({ slug: 'This slug is already taken. Please choose a different one.' });
        } else if (errorMessage.includes('name')) {
          setCategoryErrors({ name: 'This name is already taken. Please choose a different one.' });
        } else if (errorMessage.includes('Category not found')) {
          alert('❌ Parent category not found. Please refresh and try again.');
        } else {
          alert(`❌ Failed to create subcategory: ${errorMessage}`);
        }
      } else {
        alert(`❌ Failed to create subcategory: ${error.response?.data?.error?.message || error.message}`);
      }
    },
  });

  const createSubSubcategoryMutation = useMutation({
    mutationFn: (data) => formsAPI.subSubcategories.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['forms-categories']);
      queryClient.invalidateQueries(['forms-categories-tree']);
      setShowModal(false);
      resetCategoryForm();
      
      // Show success message
      const subSubcategoryName = response.data?.data?.subSubcategory?.name || 'Sub-subcategory';
      alert(`✅ ${subSubcategoryName} created successfully!`);
    },
    onError: (error) => {
      console.error('Create sub-subcategory error:', error);
      setIsSubmittingCategory(false);
      
      // Handle validation errors
      if (error.response?.status === 400 && error.response?.data?.error?.message) {
        const errorMessage = error.response.data.error.message;
        if (errorMessage.includes('slug')) {
          setCategoryErrors({ slug: 'This slug is already taken. Please choose a different one.' });
        } else if (errorMessage.includes('name')) {
          setCategoryErrors({ name: 'This name is already taken. Please choose a different one.' });
        } else if (errorMessage.includes('Subcategory not found')) {
          alert('❌ Parent subcategory not found. Please refresh and try again.');
        } else {
          alert(`❌ Failed to create sub-subcategory: ${errorMessage}`);
        }
      } else {
        alert(`❌ Failed to create sub-subcategory: ${error.response?.data?.error?.message || error.message}`);
      }
    },
  });

  // Category management mutations
  const updateCategoryMutation = useMutation({
    mutationFn: ({ id, data }) => formsAPI.categories.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['forms-categories']);
      queryClient.invalidateQueries(['forms-categories-tree']);
      setShowModal(false);
      resetCategoryForm();
    },
    onError: (error) => {
      console.error('Update category error:', error);
      alert('Failed to update category: ' + (error.response?.data?.error?.message || error.message));
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: ({ id, type }) => {
      switch (type) {
        case 'category':
          return formsAPI.categories.delete(id);
        case 'subcategory':
          return formsAPI.subcategories.delete(id);
        case 'sub-subcategory':
          return formsAPI.subSubcategories.delete(id);
        default:
          throw new Error('Invalid type');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['forms-categories']);
      queryClient.invalidateQueries(['forms-categories-tree']);
    },
    onError: (error) => {
      console.error('Delete category error:', error);
      alert('Failed to delete category: ' + (error.response?.data?.error?.message || error.message));
    },
  });

  // Form action handlers
  const handleCreate = () => {
    setEditingForm(null);
    setModalType('upload');
    resetForm();
    setShowModal(true);
  };

  const handleCreateCategory = (parent = null) => {
    setParentCategory(parent);
    setModalType('category');
    setEditingCategory(null);
    resetCategoryForm();
    setShowModal(true);
  };

  const handleCreateSubcategory = (parentCategory) => {
    // If parentCategory is null or doesn't have id, try to find it from category tree data
    if (!parentCategory || !parentCategory.id) {
      // Try to find the parent category from the category tree data using the category ID from path
      if (currentPath.length > 0 && categoryTreeData?.data?.data) {
        const categoryId = currentPath[0].id; // Get the category ID from the path
        const foundCategory = categoryTreeData.data.data.find(cat => cat.id === categoryId);
        if (foundCategory) {
          setParentCategory(foundCategory);
          setModalType('subcategory');
          setEditingCategory(null);
          resetCategoryForm(false); // Don't clear parent category
          setShowModal(true);
          return;
        }
      }
      
      // Last resort: try to create a minimal parent category object using the category ID from path
      if (currentPath.length > 0) {
        const categoryId = currentPath[0].id;
        const minimalParent = { id: categoryId, name: 'Parent Category' };
        setParentCategory(minimalParent);
        setModalType('subcategory');
        setEditingCategory(null);
        resetCategoryForm(false); // Don't clear parent category
        setShowModal(true);
        return;
      }
      
      console.error('Invalid parent category:', parentCategory);
      alert('Cannot create subcategory: Parent category not found. Please navigate to a category first.');
      return;
    }
    
    setParentCategory(parentCategory);
    setModalType('subcategory');
    setEditingCategory(null);
    resetCategoryForm(false); // Don't clear parent category
    setShowModal(true);
  };

  const handleCreateSubSubcategory = (parentSubcategory) => {
    setParentCategory(parentSubcategory);
    setModalType('sub-subcategory');
    setEditingCategory(null);
    resetCategoryForm(false); // Don't clear parent category
    setShowModal(true);
  };

  const handleCreateFolder = () => {
    if (currentLevel === 'categories') {
      handleCreateCategory();
    } else if (currentLevel === 'subcategories') {
      // When creating a subcategory, we need the parent category from the path
      const parentCategory = currentPath.length > 0 ? currentPath[0].fullObject : null;
      handleCreateSubcategory(parentCategory);
    } else if (currentLevel === 'sub-subcategories') {
      // When creating a sub-subcategory, we need the parent subcategory from the path
      const parentSubcategory = currentPath.length > 1 ? currentPath[1].fullObject : null;
      handleCreateSubSubcategory(parentSubcategory);
    }
  };

  const handleEditCategory = (category, type) => {
    setEditingCategory(category);
    setModalType(type);
    setCategoryData({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      icon: category.icon || '',
      color: category.color || '#3B82F6',
      sortOrder: category.sortOrder || 0,
      isActive: category.isActive
    });
    setShowModal(true);
  };

  const handleDeleteCategory = (id, type, name) => {
    if (confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      deleteCategoryMutation.mutate({ id, type });
    }
  };

  const handleEdit = (form) => {
    setEditingForm(form);
    setModalType('edit-metadata');
    setFormData({
      title: form.title,
      category: form.category || '',
      language: form.language || 'en',
      isPublic: form.is_public,
      file: null, // File is not updated during metadata edit
      categoryId: form.categoryInfo?.categoryId || '',
      subcategoryId: form.categoryInfo?.subcategoryId || '',
      subSubcategoryId: form.categoryInfo?.subSubcategoryId || '',
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (confirm('Are you sure you want to delete this form?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleDownload = async (presign) => {
    try {
      const token = localStorage.getItem('srpf_auth_token');
      if (!token) {
        alert('You must be logged in to download forms.');
        return;
      }
      // Use presign as the download URL (absolute or relative)
      const url = presign?.startsWith('http')
        ? presign
        : `${import.meta.env.VITE_API_URL || 'http://localhost:4000'}${presign}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to download form PDF');
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      // Try to extract filename from Content-Disposition header
      const disposition = response.headers.get('Content-Disposition');
      let filename = 'form.pdf';
      if (disposition && disposition.includes('filename=')) {
        filename = disposition.split('filename=')[1].replace(/"/g, '').trim();
        if (!filename.toLowerCase().endsWith('.pdf')) {
          filename = filename.replace(/\.[^/.]+$/, '') + '.pdf';
        }
      }
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Error downloading PDF. Please try again.');
    }
  };

  const handleView = async (presingn) => {
    if (!presingn) {
      alert("No file URL available for this form.");
      return;
    }
    // If presingn is a relative URL, prepend API base
    const url = presingn.startsWith("http") ? presingn : `${import.meta.env.VITE_API_URL || 'http://localhost:4000'}${presingn}`;

    // Open the file in a new tab with Authorization header by fetching the blob
    try {
      const token = localStorage.getItem('srpf_auth_token');
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch file');
      }
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      // Optionally revoke the blob URL after some time
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
    } catch (error) {
      alert("Failed to open file: " + error.message);
    }
  };


  const handleSubmit = (e) => {
    e.preventDefault();

    if (modalType === 'upload') {
      if (editingForm) {
        // Update existing form (metadata only)
        updateMutation.mutate({
          id: editingForm.id,
          data: {
            title: formData.title,
            category: formData.category, // This might be deprecated, rely on categoryId fields
            language: formData.language,
            isPublic: formData.isPublic,
            categoryId: formData.categoryId ? parseInt(formData.categoryId) : null,
            subcategoryId: formData.subcategoryId ? parseInt(formData.subcategoryId) : null,
            subSubcategoryId: formData.subSubcategoryId ? parseInt(formData.subSubcategoryId) : null,
          },
        });
      } else {
        // Create new form (with file)
        const submitFormData = new FormData();
        submitFormData.append('title', formData.title);
        // Don't send empty category field, use hierarchical fields instead
        if (formData.category) submitFormData.append('category', formData.category);
        submitFormData.append('language', formData.language);
        submitFormData.append('isPublic', formData.isPublic.toString());
        // Add formName for WhatsApp bot (use title as formName)
        submitFormData.append('formName', formData.title);
        if (formData.categoryId) submitFormData.append('categoryId', parseInt(formData.categoryId));
        if (formData.subcategoryId) submitFormData.append('subcategoryId', parseInt(formData.subcategoryId));
        if (formData.subSubcategoryId) submitFormData.append('subSubcategoryId', parseInt(formData.subSubcategoryId));
        if (formData.file) submitFormData.append('file', formData.file);


        createMutation.mutate(submitFormData);
      }
    } else {
      // Handle category creation/update with validation
      setIsSubmittingCategory(true);
      setCategoryErrors({});

      // Auto-generate slug if not provided
      const submitData = {
        ...categoryData,
        slug: categoryData.slug || generateSlug(categoryData.name)
      };

      // Validate the data
      const validationErrors = validateCategoryData(submitData, modalType);
      if (Object.keys(validationErrors).length > 0) {
        setCategoryErrors(validationErrors);
        setIsSubmittingCategory(false);
        return;
      }

      if (editingCategory) {
        updateCategoryMutation.mutate({
          id: editingCategory.id,
          data: submitData
        });
      } else {
        switch (modalType) {
          case 'category':
            createCategoryMutation.mutate(submitData);
            break;
          case 'subcategory':
            if (!parentCategory || !parentCategory.id) {
              alert('❌ Cannot create subcategory: Parent category is not selected');
              setIsSubmittingCategory(false);
              return;
            }
            createSubcategoryMutation.mutate({ ...submitData, categoryId: parentCategory.id });
            break;
          case 'sub-subcategory':
            if (!parentCategory || !parentCategory.id) {
              alert('❌ Cannot create sub-subcategory: Parent subcategory is not selected');
              setIsSubmittingCategory(false);
              return;
            }
            createSubSubcategoryMutation.mutate({ ...submitData, subcategoryId: parentCategory.id });
            break;
          default:
            setIsSubmittingCategory(false);
            break;
        }
      }
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const validateAndSetFile = (file) => {
    setFileError('');

    if (file.type !== 'application/pdf') {
      setFileError('Only PDF files are allowed');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setFileError('File size must be less than 10MB');
      return;
    }

    setFormData(prev => ({ ...prev, file }));
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      validateAndSetFile(file);
    }
  };

  const removeFile = () => {
    setFormData(prev => ({ ...prev, file: null }));
    setFileError('');
  };

  const handleCloseModal = () => {
    setIsModalClosing(true);
    setTimeout(() => {
      setShowModal(false);
      setIsModalClosing(false);
      resetForm();
      resetCategoryForm();
    }, 300);
  };

  const handleUploadHere = (item) => {
    // Build hierarchical context based on current navigation level
    let categoryId = '';
    let subcategoryId = '';
    let subSubcategoryId = '';

    if (currentLevel === 'categories') {
      categoryId = String(item.id);
    } else if (currentLevel === 'subcategories') {
      categoryId = String(currentPath[0]?.id || '');
      subcategoryId = String(item.id);
    } else if (currentLevel === 'sub-subcategories') {
      categoryId = String(currentPath[0]?.id || '');
      subcategoryId = String(currentPath[1]?.id || '');
      subSubcategoryId = String(item.id);
    }


    // Set modal state first
    setModalType('upload');
    setEditingForm(null);

    // Set pending form data to be applied after modal opens
    setPendingFormData({
      title: '',
      category: '',
      language: 'en',
      isPublic: true,
      file: null,
      categoryId,
      subcategoryId,
      subSubcategoryId,
    });

    setShowModal(true);
  };

  const getLanguageBadgeVariant = (language) => {
    switch (language) {
      case 'en':
        return 'primary';
      case 'mr':
        return 'success';
      default:
        return 'default';
    }
  };

  // Render hierarchical category grid
  const renderCategoryGrid = () => {
    if (categoriesLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
        </div>
      );
    }

    // Ensure currentData is always an array
    const safeCurrentData = Array.isArray(currentData) ? currentData : [];

    if (!safeCurrentData || safeCurrentData.length === 0) {
      const emptyMessage = currentLevel === 'categories'
        ? 'No categories'
        : currentLevel === 'subcategories'
          ? 'No subcategories'
          : 'No sub-subcategories';
      const createMessage = currentLevel === 'categories'
        ? 'Create your first category to organize forms'
        : currentLevel === 'subcategories'
          ? 'Create your first subcategory'
          : 'Create your first sub-subcategory';

      return (
        <div className="text-center py-12">
          <Folder className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">{emptyMessage}</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            {createMessage}
          </p>
          {hasCapability('canManageCategories') && (
            <Button
              onClick={() => {
                if (currentLevel === 'categories') {
                  handleCreateCategory();
                } else if (currentLevel === 'subcategories') {
                  // When creating a subcategory, we need the parent category from the path
                  const parentCategory = currentPath.length > 0 ? currentPath[0].fullObject : null;
                  handleCreateSubcategory(parentCategory);
                } else if (currentLevel === 'sub-subcategories') {
                  // When creating a sub-subcategory, we need the parent subcategory from the path
                  const parentSubcategory = currentPath.length > 1 ? currentPath[1].fullObject : null;
                  handleCreateSubSubcategory(parentSubcategory);
                }
              }}
              className="flex items-center gap-2 mx-auto"
            >
              <Plus className="w-4 h-4" />
              Create {currentLevel === 'categories' ? 'Category' : currentLevel === 'subcategories' ? 'Subcategory' : 'Sub-subcategory'}
            </Button>
          )}
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {safeCurrentData.map((item) => {
          const canNavigate = (currentLevel === 'categories' && (item.subcategories?.length > 0 || item.formCount > 0)) ||
            (currentLevel === 'subcategories' && (item.subSubcategories?.length > 0 || item.formCount > 0)) ||
            (currentLevel === 'sub-subcategories' && item.formCount > 0);


          return (
            <div
              key={item.id}
              className={`group relative p-5 rounded-xl border transition-all duration-200 min-h-[180px] flex flex-col ${dragActive && item.id === currentParentId
                ? 'border-teal-400 bg-teal-50 dark:bg-teal-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-teal-300 dark:hover:border-teal-600 hover:bg-gray-50 dark:hover:bg-gray-800 hover:shadow-md'
                }`}
              onClick={() => {
                if (canNavigate) {
                  navigateTo(item, currentLevel === 'categories' ? 'category' : 'subcategory');
                } else {
                  // If it's a leaf node, open upload modal for this level
                  handleUploadHere(item);
                }
              }}
              onDragOver={(e) => handleDrag(e)}
              onDragLeave={(e) => handleDrag(e)}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragActive(false);
                const files = e.dataTransfer.files;
                if (files && files.length > 0) {
                  const file = files[0];
                  if (file.type === 'application/pdf') {
                    // Set the file first, then call handleUploadHere
                    setFormData(prev => ({
                      ...prev,
                      file,
                    }));
                    handleUploadHere(item);
                  } else {
                    alert('Please upload only PDF files.');
                  }
                }
              }}
            >
              {/* Folder Icon */}
              <div className="flex items-center justify-center mb-3 flex-shrink-0">
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center shadow-sm"
                  style={{ backgroundColor: item.color + '15', color: item.color }}
                >
                  {canNavigate ? (
                    <FolderOpen className="w-7 h-7" />
                  ) : (
                    <Folder className="w-7 h-7" />
                  )}
                </div>
              </div>

              {/* Folder Info */}
              <div className="text-center flex-grow flex flex-col justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2 text-base leading-tight">
                    {item.name}
                  </h3>
                  {item.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 leading-relaxed">
                      {item.description}
                    </p>
                  )}
                </div>
                <div className="mt-3">
                  {canNavigate ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400">
                      Browse
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      {item.formCount || 0} items
                    </span>
                  )}
                </div>
              </div>

              {/* Upload Here Button - only show for leaf nodes */}
              {!canNavigate && hasCapability('canUploadForms') && (
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUploadHere(item);
                    }}
                    className="bg-white text-black dark:text-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700"
                    size="sm"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Here
                  </Button>
                </div>
              )}

              {/* Subtle Action Menu */}
              {hasCapability('canManageCategories') && (
                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-all duration-200">
                  <div className="flex gap-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditCategory(item, currentLevel === 'categories' ? 'category' : currentLevel === 'subcategories' ? 'subcategory' : 'sub-subcategory');
                      }}
                      className="p-1.5 rounded-md bg-white/90 hover:bg-white shadow-sm border border-gray-200 hover:border-gray-300 transition-all duration-200"
                      title="Edit folder"
                    >
                      <Edit className="w-3.5 h-3.5 text-gray-600 hover:text-gray-800" />
                    </button>
                    {currentLevel === 'categories' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCreateSubcategory(item);
                        }}
                        className="p-1.5 rounded-md bg-white/90 hover:bg-white shadow-sm border border-gray-200 hover:border-gray-300 transition-all duration-200"
                        title="Create subcategory"
                      >
                        <FolderPlus className="w-3.5 h-3.5 text-gray-600 hover:text-gray-800" />
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCategory(item.id, currentLevel === 'categories' ? 'category' : currentLevel === 'subcategories' ? 'subcategory' : 'sub-subcategory', item.name);
                      }}
                      className="p-1.5 rounded-md bg-white/90 hover:bg-white shadow-sm border border-gray-200 hover:border-red-300 transition-all duration-200"
                      title="Delete folder"
                    >
                      <Trash2 className="w-3.5 h-3.5 text-gray-600 hover:text-red-600" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Add New Folder Card */}
        {hasCapability('canManageCategories') && (
          <div
            className="group relative p-6 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-teal-400 dark:hover:border-teal-500 cursor-pointer transition-all duration-200 hover:bg-teal-50 dark:hover:bg-teal-900/10"
            onClick={() => {
              if (currentLevel === 'categories') {
                handleCreateCategory();
              } else if (currentLevel === 'subcategories') {
                // When creating a subcategory, we need the parent category from the path
                const parentCategory = currentPath.length > 0 ? currentPath[0].fullObject : null;
                handleCreateSubcategory(parentCategory);
              } else if (currentLevel === 'sub-subcategories') {
                // When creating a sub-subcategory, we need the parent subcategory from the path
                const parentSubcategory = currentPath.length > 1 ? currentPath[1].fullObject : null;
                handleCreateSubSubcategory(parentSubcategory);
              }
            }}
          >
            <div className="flex flex-col items-center justify-center h-full min-h-[160px]">
              <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-4 group-hover:bg-teal-100 dark:group-hover:bg-teal-900/20 transition-colors">
                <Plus className="w-8 h-8 text-gray-400 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2 text-lg">
                New Folder
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center leading-relaxed">
                Create a new folder to organize your forms
              </p>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <style jsx>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
      <div className="h-full px-6 py-6 overflow-y-auto dark:text-white">
        {/* Header */}
        <div className="mb-8">
          {currentPath.length > 0 && (
            <div className="mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={navigateBack}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
            </div>
          )}
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Forms</h1>
              <p className="text-gray-600 dark:text-gray-400 text-lg">Organize and manage your forms</p>
            </div>
            <div className="flex items-center gap-3">
            {/* View Toggle */}
            <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => setViewMode('hierarchical')}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${viewMode === 'hierarchical'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
              >
                <Folder className="w-4 h-4 inline mr-2" />
                Folders
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${viewMode === 'list'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
              >
                <List className="w-4 h-4 inline mr-2" />
                List
              </button>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              {hasCapability('canManageCategories') && (
                <Button
                  onClick={handleCreateFolder}
                  variant="outline"
                  className="flex items-center gap-2 border-teal-200 text-teal-700 hover:bg-teal-50 dark:border-teal-800 dark:text-teal-400 dark:hover:bg-teal-900/20"
                >
                  <Folder size={16} />
                  New Folder
                </Button>
              )}
              {/* <Button
                onClick={() => setShowBulkUpload(true)}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Upload size={16} />
                Bulk Upload
              </Button> */}
              <Button onClick={handleCreate} className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white">
                <Plus size={16} />
                New Form
              </Button>
            </div>
          </div>
        </div>
        </div>

        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Search forms..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="pl-10 pr-4 py-3 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200"
            />
            {filters.search && (
              <button
                onClick={() => handleFilterChange('search', '')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Breadcrumb Navigation */}
        {viewMode === 'hierarchical' && currentPath.length > 0 && (
          <div className="mb-6">
            <nav className="flex items-center gap-2 text-sm">
              <button
                onClick={navigateToRoot}
                className="flex items-center gap-1 text-gray-500 hover:text-teal-600 dark:text-gray-400 dark:hover:text-teal-400 transition-colors"
              >
                <Home className="w-4 h-4" />
                <span>Home</span>
              </button>
              {currentPath.map((item, index) => (
                <React.Fragment key={item.id}>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                  <span className={`${index === currentPath.length - 1
                    ? 'text-gray-900 dark:text-white font-medium'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white cursor-pointer'
                    }`}>
                    {item.name}
                  </span>
                </React.Fragment>
              ))}
            </nav>
          </div>
        )}

        {/* Main Content */}
        <div className="w-full">
          {viewMode === 'hierarchical' ? (
            /* Hierarchical View - File Manager Style */
            <Card>
              <div className="p-6">
                {/* Navigation Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {currentLevel === 'categories' ? 'Categories' :
                        currentLevel === 'subcategories' ? 'Subcategories' :
                          'Sub-subcategories'}
                    </h3>

                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {currentLevel === 'categories'
                        ? 'Click on categories to browse subcategories, or drag and drop files to upload'
                        : 'Click on items to navigate deeper, or drag and drop files to upload'
                      }
                    </p>
                  </div>

                  {hasActiveFilters() && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearFilters}
                      className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      <X className="w-4 h-4" />
                      Clear Filters
                    </Button>
                  )}
                </div>
                {renderCategoryGrid()}
              </div>
            </Card>
          ) : (
            /* List View - Traditional Forms List */
            <Card>
              {/* Navigation Header for List View */}
              <div className="p-6 pb-0">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Forms List
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {currentPath.length > 0 
                        ? `Showing forms in: ${currentPath.map(p => p.name).join(' > ')}`
                        : 'All forms in the system'
                      }
                    </p>
                  </div>

                  {hasActiveFilters() && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearFilters}
                      className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      <X className="w-4 h-4" />
                      Clear Filters
                    </Button>
                  )}
                </div>
              </div>

              {formsError ? (
                <div className="text-center py-8">
                  <div className="text-red-600 dark:text-red-400">
                    <h3 className="text-lg font-medium">Error loading forms</h3>
                    <p className="text-sm mt-2">{formsError.message}</p>
                  </div>
                </div>
              ) : formsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
                </div>
              ) : formsData.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No forms</h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Get started by uploading your first form.
                  </p>
                  <div className="mt-6">
                    <Button onClick={handleCreate} className="flex items-center gap-2 mx-auto">
                      <Upload size={16} />
                      Upload Form
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Form
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Language
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Visibility
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Uploaded
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {formsData.map((form) => (
                        <tr key={form.id} className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {form.title}
                              </div>
                              {form.categoryInfo && (
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {form.categoryInfo.categoryName}
                                  {form.categoryInfo.subcategoryName && ` > ${form.categoryInfo.subcategoryName}`}
                                  {form.categoryInfo.subSubcategoryName && ` > ${form.categoryInfo.subSubcategoryName}`}
                                </div>
                              )}
                              {!form.categoryInfo && form.category && (
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {form.category}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge variant={getLanguageBadgeVariant(form.language)}>
                              {form.language === 'en' ? 'English' : 'Marathi'}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge variant={form.is_public ? 'success' : 'warning'}>
                              {form.is_public ? 'Public' : 'Private'}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {formatDate(form.created_at)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  handleView(form.presign);
                                }}
                                className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                                title="View PDF"
                              >
                                <Eye size={16} />
                              </Button>

                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownload(form.presign)}
                                className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                                title="Download"
                              >
                                <Download size={16} />
                              </Button>
                              {hasCapability('canUploadForms') && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEdit(form)}
                                  className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 border-blue-600"
                                  title="Edit Metadata"
                                >
                                  <Edit size={16} />
                                </Button>
                              )}
                              {hasCapability('canDeleteForms') && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDelete(form.id)}
                                  className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 border-red-600"
                                  title="Delete"
                                >
                                  <Trash2 size={16} />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 px-4 pb-4">
                  <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                    <span>
                      Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} forms
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="secondary"
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                        disabled={pagination.page === 1}
                        className="px-2 py-1 text-xs"
                      >
                        Previous
                      </Button>
                      <span className="px-2 py-1">
                        Page {pagination.page} of {pagination.pages}
                      </span>
                      <Button
                        variant="secondary"
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                        disabled={pagination.page === pagination.pages}
                        className="px-2 py-1 text-xs"
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          )}
        </div>

        {/* Enhanced Side Pane Modal */}
        {showModal && (
          <div
            className={`fixed inset-0 backdrop-blur-md flex items-center justify-end z-50 transition-all duration-300 ease-out ${isModalClosing ? 'bg-black/0' : 'bg-black/50'
              }`}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                handleCloseModal();
              }
            }}
          >
            <div
              className="bg-white dark:bg-gray-900 h-full w-full max-w-5xl shadow-2xl dark:shadow-2xl transform transition-all duration-500 ease-out overflow-hidden flex flex-col"
              style={{
                transform: isModalClosing ? 'translateX(100%)' : 'translateX(0)',
                animation: showModal && !isModalClosing ? 'slideInRight 0.5s ease-out' : 'none'
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {/* Enhanced Header */}
              <div className="sticky top-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border-b border-gray-200/50 dark:border-gray-700/50 p-2 shadow-lg z-10">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <div className="p-2">
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {modalType === 'upload'
                          ? (editingForm ? 'Edit Form Metadata' : 'Upload New Form')
                          : editingCategory
                            ? `Edit ${modalType === 'category' ? 'Category' : modalType === 'subcategory' ? 'Subcategory' : 'Sub-subcategory'}`
                            : modalType === 'category'
                              ? 'Create New Category'
                              : modalType === 'subcategory'
                                ? 'Create New Subcategory'
                                : 'Create New Sub-subcategory'
                        }
                      </h2>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {modalType === 'upload'
                          ? (editingForm ? 'Update form details and settings (file cannot be changed)' : 'Upload a new form document to the system')
                          : editingCategory
                            ? `Update ${modalType === 'category' ? 'category' : modalType === 'subcategory' ? 'subcategory' : 'sub-subcategory'} details`
                            : modalType === 'category'
                              ? 'Create a new form category'
                              : modalType === 'subcategory'
                                ? `Create a new subcategory under "${currentParent?.name}"`
                                : `Create a new sub-subcategory under "${currentParent?.name}"`
                        }
                      </p>


                      {/* Hierarchical Path Display for Upload */}
                      {modalType === 'upload' && !editingForm && (formData.categoryId || formData.subcategoryId || formData.subSubcategoryId) && (
                        <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                          <div className="flex items-center gap-2 text-sm">
                            <Folder className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            <span className="text-blue-800 dark:text-blue-200 font-medium">Uploading to:</span>
                            <div className="flex items-center gap-1 text-blue-700 dark:text-blue-300">
                              {currentPath.map((pathItem, index) => (
                                <React.Fragment key={index}>
                                  <span>{pathItem.name}</span>
                                  {index < currentPath.length - 1 && <ChevronRight className="w-3 h-3" />}
                                </React.Fragment>
                              ))}
                              {formData.subSubcategoryId && currentPath.length === 2 && (
                                <>
                                  <ChevronRight className="w-3 h-3" />
                                  <span>{getCurrentLevelData().find(item => item.id === formData.subSubcategoryId)?.name || 'Current Level'}</span>
                                </>
                              )}
                              {formData.subcategoryId && currentPath.length === 1 && (
                                <>
                                  <ChevronRight className="w-3 h-3" />
                                  <span>{getCurrentLevelData().find(item => item.id === formData.subcategoryId)?.name || 'Current Level'}</span>
                                </>
                              )}
                              {formData.categoryId && currentPath.length === 0 && (
                                <>
                                  <ChevronRight className="w-3 h-3" />
                                  <span>{getCurrentLevelData().find(item => item.id === formData.categoryId)?.name || 'Current Level'}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCloseModal}
                    className="p-3 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all duration-200 group"
                  >
                    <X className="w-5 h-5 text-gray-500 group-hover:text-gray-700 dark:text-gray-400 dark:group-hover:text-200 transition-colors duration-200" />
                  </Button>
                </div>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto">
                <div className="p-8 space-y-8">
                  <form onSubmit={handleSubmit} className="space-y-8">
                    {modalType === 'upload' ? (
                      <>
                        {/* Enhanced Form Information */}
                        <div className="relative">
                          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-indigo-500/10 rounded-2xl blur-xl"></div>
                          <div className="relative bg-white dark:bg-gray-800 p-6 rounded-2xl border border-blue-200/50 dark:border-blue-700/50 shadow-lg">
                            <div className="flex items-center gap-3 mb-6">
                              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                                <FileText className="w-4 h-4 text-white" />
                              </div>
                              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Form Information</h3>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              {/* Enhanced Title Input */}
                              <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                                  <FileText className="w-4 h-4" />
                                  Form Title
                                </label>
                                <input
                                  value={formData.title}
                                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                  placeholder="Enter form title..."
                                  className="w-full px-4 py-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-white shadow-sm"
                                  required
                                />
                              </div>

                              {/* Hierarchical Category Selection */}
                              <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                                  <Folder className="w-4 h-4" />
                                  Form Category
                                  {(formData.categoryId || formData.subcategoryId || formData.subSubcategoryId) && (
                                    <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full">
                                      Auto-filled from current path
                                    </span>
                                  )}
                                </label>
                                <div className="grid grid-cols-1 gap-3">
                                  {/* Main Category */}
                                  <select
                                    value={formData.categoryId}
                                    onChange={(e) => {
                                      setFormData(prev => ({
                                        ...prev,
                                        categoryId: e.target.value,
                                        subcategoryId: '',
                                        subSubcategoryId: ''
                                      }));
                                    }}
                                    className="w-full px-4 py-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-white shadow-sm"
                                  >
                                    <option value="">Select Main Category</option>
                                    {categoryTreeData?.data?.data?.map((category) => (
                                        <option key={category.id} value={String(category.id)}>
                                          {category.name}
                                        </option>
                                    ))}
                                  </select>

                                  {/* Subcategory */}
                                  {formData.categoryId && (
                                    <select
                                      value={formData.subcategoryId}
                                      onChange={(e) => {
                                        setFormData(prev => ({
                                          ...prev,
                                          subcategoryId: e.target.value,
                                          subSubcategoryId: ''
                                        }));
                                      }}
                                      className="w-full px-4 py-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-white shadow-sm"
                                    >
                                      <option value="">Select Subcategory</option>
                                      {categoryTreeData?.data?.data?.find(cat => String(cat.id) === formData.categoryId)?.subcategories?.map((subcategory) => (
                                        <option key={subcategory.id} value={String(subcategory.id)}>
                                          {subcategory.name}
                                        </option>
                                      ))}
                                    </select>
                                  )}

                                  {/* Sub-subcategory */}
                                  {formData.subcategoryId && (
                                    <select
                                      value={formData.subSubcategoryId}
                                      onChange={(e) => setFormData(prev => ({ ...prev, subSubcategoryId: e.target.value }))}
                                      className="w-full px-4 py-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-white shadow-sm"
                                    >
                                      <option value="">Select Sub-subcategory (Optional)</option>
                                      {categoryTreeData?.data?.data
                                        ?.find(cat => String(cat.id) === formData.categoryId)
                                        ?.subcategories
                                        ?.find(sub => String(sub.id) === formData.subcategoryId)
                                        ?.subSubcategories?.map((subSubcategory) => (
                                          <option key={subSubcategory.id} value={String(subSubcategory.id)}>
                                            {subSubcategory.name}
                                          </option>
                                        ))}
                                    </select>
                                  )}
                                </div>

                                {/* Auto-fill notice */}
                                {(formData.categoryId || formData.subcategoryId || formData.subSubcategoryId) && (
                                  <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                                    <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-300">
                                      <CheckCircle className="w-4 h-4" />
                                      <span>Category path automatically filled from current location. You can change these if needed.</span>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Enhanced Language Selection */}
                              <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                                  </svg>
                                  Language
                                </label>
                                <div className="relative">
                                  <select
                                    value={formData.language}
                                    onChange={(e) => setFormData(prev => ({ ...prev, language: e.target.value }))}
                                    className="w-full px-4 py-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-white shadow-sm appearance-none cursor-pointer"
                                    required
                                  >
                                    <option value="en">🇺🇸 English</option>
                                    <option value="mr">🇮🇳 Marathi</option>
                                  </select>
                                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                    <ChevronDown className="w-5 h-5 text-gray-400" />
                                  </div>
                                </div>
                              </div>

                              {/* Enhanced Visibility Selection */}
                              <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                                  <Eye className="w-4 h-4" />
                                  Visibility
                                </label>
                                <div className="relative">
                                  <select
                                    value={formData.isPublic}
                                    onChange={(e) => setFormData(prev => ({ ...prev, isPublic: e.target.value === 'true' }))}
                                    className="w-full px-4 py-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-white shadow-sm appearance-none cursor-pointer"
                                  >
                                    <option value="true">🌐 Public - Visible to all users</option>
                                    <option value="false">🔒 Private - Restricted access</option>
                                  </select>
                                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                    <ChevronDown className="w-5 h-5 text-gray-400" />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Enhanced File Upload Section (only for new forms) */}
                        {!editingForm && (
                          <div className="relative">
                            <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-2xl blur-xl"></div>
                            <div className="relative bg-white dark:bg-gray-800 p-6 rounded-2xl border border-green-200/50 dark:border-green-700/50 shadow-lg">
                              <div className="flex items-center gap-3 mb-6">
                                <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                                  <Upload className="w-4 h-4 text-white" />
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">File Upload</h3>
                              </div>

                              {/* Drag and Drop Area */}
                              <div
                                className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 ${dragActive
                                  ? 'border-green-400 bg-green-50 dark:bg-green-900/20'
                                  : 'border-gray-300 dark:border-gray-600 hover:border-green-400 hover:bg-green-50/50 dark:hover:bg-green-900/10'
                                  }`}
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                              >
                                <input
                                  type="file"
                                  onChange={handleFileChange}
                                  accept=".pdf"
                                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                  required={!editingForm}
                                />

                                {formData.file ? (
                                  <div className="space-y-4">
                                    <div className="flex items-center justify-center">
                                      <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                                        <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                                      </div>
                                    </div>
                                    <div>
                                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                        File Selected Successfully
                                      </h4>
                                      <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                                        <div className="flex items-center gap-3">
                                          <File className="w-8 h-8 text-red-500" />
                                          <div className="flex-1 text-left">
                                            <p className="font-medium text-gray-900 dark:text-white">
                                              {formData.file.name}
                                            </p>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                              {(formData.file.size / 1024 / 1024).toFixed(2)} MB
                                            </p>
                                          </div>
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={removeFile}
                                            className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                                          >
                                            <X className="w-4 h-4" />
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="space-y-4">
                                    <div className="flex items-center justify-center">
                                      <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-colors duration-200 ${dragActive
                                        ? 'bg-green-100 dark:bg-green-900/30'
                                        : 'bg-gray-100 dark:bg-gray-700'
                                        }`}>
                                        <Upload className={`w-8 h-8 transition-colors duration-200 ${dragActive
                                          ? 'text-green-600 dark:text-green-400'
                                          : 'text-gray-400'
                                          }`} />
                                      </div>
                                    </div>
                                    <div>
                                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                        {dragActive ? 'Drop your file here' : 'Upload Form Document'}
                                      </h4>
                                      <p className="text-gray-600 dark:text-gray-400 mb-4">
                                        Drag and drop your PDF file here, or click to browse
                                      </p>
                                      <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                                        <File className="w-4 h-4" />
                                        <span>PDF files only (max 10MB)</span>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {fileError && (
                                  <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                                    <div className="flex items-center gap-2">
                                      <AlertCircle className="w-4 h-4 text-red-500" />
                                      <span className="text-sm text-red-700 dark:text-red-300">{fileError}</span>
                                    </div>
                                  </div>
                                )}
                              </div>

                              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg">
                                <p className="text-sm text-blue-800 dark:text-blue-200">
                                  <strong>Supported Formats:</strong> PDF documents only
                                </p>
                                <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                                  Maximum file size: 10MB. Ensure your document is clear and readable.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Enhanced Action Buttons */}
                        <div className="relative">
                          <div className="flex justify-end gap-4">
                            <Button
                              type="button"
                              variant="secondary"
                              onClick={handleCloseModal}
                              className="px-6 py-3 rounded-xl transition-all duration-200 hover:scale-105 border-gray-300 hover:border-gray-400 shadow-sm"
                            >
                              Cancel
                            </Button>
                            <Button
                              type="submit"
                              disabled={createMutation.isPending || updateMutation.isPending}
                              className="px-8 py-3 text-white border-0 rounded-xl transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg font-semibold"
                            >
                              {createMutation.isPending || updateMutation.isPending ? (
                                <div className="flex items-center gap-2">
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                  {editingForm ? 'Updating...' : 'Uploading...'}
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  {editingForm ? 'Update Form' : 'Upload Form'}
                                  <Upload className="w-4 h-4" />
                                </div>
                              )}
                            </Button>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Category Creation Form */}
                        <div className="relative">
                          <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-2xl blur-xl"></div>
                          <div className="relative bg-white dark:bg-gray-800 p-6 rounded-2xl border border-green-200/50 dark:border-green-700/50 shadow-lg">
                            <div className="flex items-center gap-3 mb-6">
                              <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                                <Folder className="w-4 h-4 text-white" />
                              </div>
                              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Category Information</h3>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                                  Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                  value={categoryData.name}
                                  onChange={(e) => {
                                    setCategoryData(prev => ({ ...prev, name: e.target.value }));
                                    // Clear name error when user starts typing
                                    if (categoryErrors.name) {
                                      setCategoryErrors(prev => ({ ...prev, name: undefined }));
                                    }
                                  }}
                                  placeholder="Enter category name"
                                  className={`w-full px-4 py-3 bg-white dark:bg-gray-700 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 text-gray-900 dark:text-white shadow-sm ${
                                    categoryErrors.name 
                                      ? 'border-red-300 dark:border-red-600' 
                                      : 'border-gray-200 dark:border-gray-600'
                                  }`}
                                  required
                                />
                                {categoryErrors.name && (
                                  <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                                    <AlertCircle className="w-4 h-4" />
                                    {categoryErrors.name}
                                  </p>
                                )}
                              </div>

                              <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                                  Slug <span className="text-red-500">*</span>
                                </label>
                                <input
                                  value={categoryData.slug}
                                  onChange={(e) => {
                                    setCategoryData(prev => ({ ...prev, slug: e.target.value }));
                                    // Clear slug error when user starts typing
                                    if (categoryErrors.slug) {
                                      setCategoryErrors(prev => ({ ...prev, slug: undefined }));
                                    }
                                  }}
                                  placeholder="auto-generated-slug"
                                  className={`w-full px-4 py-3 bg-white dark:bg-gray-700 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 text-gray-900 dark:text-white shadow-sm ${
                                    categoryErrors.slug 
                                      ? 'border-red-300 dark:border-red-600' 
                                      : 'border-gray-200 dark:border-gray-600'
                                  }`}
                                />
                                {categoryErrors.slug && (
                                  <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                                    <AlertCircle className="w-4 h-4" />
                                    {categoryErrors.slug}
                                  </p>
                                )}
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  Auto-generated from name. Only lowercase letters, numbers, and hyphens allowed.
                                </p>
                              </div>

                              <div className="md:col-span-2 space-y-2">
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                                  Description
                                </label>
                                <textarea
                                  value={categoryData.description}
                                  onChange={(e) => {
                                    setCategoryData(prev => ({ ...prev, description: e.target.value }));
                                    // Clear description error when user starts typing
                                    if (categoryErrors.description) {
                                      setCategoryErrors(prev => ({ ...prev, description: undefined }));
                                    }
                                  }}
                                  placeholder="Enter category description (optional)"
                                  className={`w-full px-4 py-3 bg-white dark:bg-gray-700 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 text-gray-900 dark:text-white shadow-sm ${
                                    categoryErrors.description 
                                      ? 'border-red-300 dark:border-red-600' 
                                      : 'border-gray-200 dark:border-gray-600'
                                  }`}
                                  rows={3}
                                />
                                {categoryErrors.description && (
                                  <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                                    <AlertCircle className="w-4 h-4" />
                                    {categoryErrors.description}
                                  </p>
                                )}
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {categoryData.description?.length || 0}/500 characters
                                </p>
                              </div>

                              <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                                  Icon
                                </label>
                                <input
                                  value={categoryData.icon}
                                  onChange={(e) => setCategoryData(prev => ({ ...prev, icon: e.target.value }))}
                                  placeholder="Icon name"
                                  className="w-full px-4 py-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 text-gray-900 dark:text-white shadow-sm"
                                />
                              </div>

                              <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                                  Color
                                </label>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="color"
                                    value={categoryData.color}
                                    onChange={(e) => {
                                      setCategoryData(prev => ({ ...prev, color: e.target.value }));
                                      // Clear color error when user changes color
                                      if (categoryErrors.color) {
                                        setCategoryErrors(prev => ({ ...prev, color: undefined }));
                                      }
                                    }}
                                    className="w-10 h-10 border border-gray-300 dark:border-gray-600 rounded cursor-pointer"
                                  />
                                  <input
                                    value={categoryData.color}
                                    onChange={(e) => {
                                      setCategoryData(prev => ({ ...prev, color: e.target.value }));
                                      // Clear color error when user starts typing
                                      if (categoryErrors.color) {
                                        setCategoryErrors(prev => ({ ...prev, color: undefined }));
                                      }
                                    }}
                                    placeholder="#3B82F6"
                                    className={`flex-1 px-4 py-3 bg-white dark:bg-gray-700 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 text-gray-900 dark:text-white shadow-sm ${
                                      categoryErrors.color 
                                        ? 'border-red-300 dark:border-red-600' 
                                        : 'border-gray-200 dark:border-gray-600'
                                    }`}
                                  />
                                </div>
                                {categoryErrors.color && (
                                  <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                                    <AlertCircle className="w-4 h-4" />
                                    {categoryErrors.color}
                                  </p>
                                )}
                              </div>

                              <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                                  Sort Order
                                </label>
                                <input
                                  type="number"
                                  min="0"
                                  value={categoryData.sortOrder}
                                  onChange={(e) => {
                                    setCategoryData(prev => ({ ...prev, sortOrder: parseInt(e.target.value) || 0 }));
                                    // Clear sort order error when user starts typing
                                    if (categoryErrors.sortOrder) {
                                      setCategoryErrors(prev => ({ ...prev, sortOrder: undefined }));
                                    }
                                  }}
                                  placeholder="0"
                                  className={`w-full px-4 py-3 bg-white dark:bg-gray-700 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 text-gray-900 dark:text-white shadow-sm ${
                                    categoryErrors.sortOrder 
                                      ? 'border-red-300 dark:border-red-600' 
                                      : 'border-gray-200 dark:border-gray-600'
                                  }`}
                                />
                                {categoryErrors.sortOrder && (
                                  <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                                    <AlertCircle className="w-4 h-4" />
                                    {categoryErrors.sortOrder}
                                  </p>
                                )}
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  Lower numbers appear first
                                </p>
                              </div>

                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  id="isActive"
                                  checked={categoryData.isActive}
                                  onChange={(e) => setCategoryData(prev => ({ ...prev, isActive: e.target.checked }))}
                                  className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                                />
                                <label htmlFor="isActive" className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                  Active
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Category Action Buttons */}
                        <div className="relative">
                          <div className="flex justify-end gap-4">
                            <Button
                              type="button"
                              variant="secondary"
                              onClick={handleCloseModal}
                              className="px-6 py-3 rounded-xl transition-all duration-200 hover:scale-105 border-gray-300 hover:border-gray-400 shadow-sm"
                            >
                              Cancel
                            </Button>
                            <Button
                              type="submit"
                              disabled={isSubmittingCategory || createCategoryMutation.isPending || createSubcategoryMutation.isPending || createSubSubcategoryMutation.isPending || updateCategoryMutation.isPending}
                              className="px-8 py-3 text-white border-0 rounded-xl transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg font-semibold"
                            >
                              {isSubmittingCategory || createCategoryMutation.isPending || createSubcategoryMutation.isPending || createSubSubcategoryMutation.isPending || updateCategoryMutation.isPending ? (
                                <div className="flex items-center gap-2">
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                  {editingCategory ? 'Updating...' : 'Creating...'}
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  {editingCategory ? 'Update' : 'Create'}
                                  <Plus className="w-4 h-4" />
                                </div>
                              )}
                            </Button>
                          </div>
                        </div>
                      </>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Upload Modal */}
        {showBulkUpload && (
          <BulkUploadForms onClose={() => setShowBulkUpload(false)} />
        )}
      </div>
    </>
  );
}

export default FormsPage;